import { db, auth, storage } from '../../utils/firebase'; // Ensure auth, db, and storage are imported
import React, { useState, useEffect } from 'react'; // Ensure useState and useEffect are imported

import { doc, setDoc, serverTimestamp, updateDoc, getDoc } from 'firebase/firestore'; // Import updateDoc
import emailjs from '@emailjs/browser';

// ✅ Add these imports for image handling
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ImageCropper from '../../components/ImageCropper'; // ✅ Assuming path is correct

// ✅ Add the new prop to the function signature
const RegisterForm = ({ user, onComplete, onRegistrationCompleteRedirect, onProfileUpdate }) => {
    const [formData, setFormData] = React.useState({
        name: user.displayName || '',
        age: '',
        gender: '',
        location: '',
        skills: {
            hiking: 'beginner',
            running: 'beginner'
        },
        bio: '',
        instagram: '',
        // ✅ Set a default image path (avatars will be removed)
        image: '/avatars/default.png' 
    });

    // ✅ Add these states for image handling (adapted from PersonalProfileView)
    const [file, setFile] = useState(null); // The selected file from input
    const [croppingURL, setCroppingURL] = useState(null); // URL for the cropper
    const [showCropper, setShowCropper] = useState(false); // Control cropper modal
    const [profileImageURL, setProfileImageURL] = useState(''); // Will store the final uploaded Firebase Storage URL
    const [uploading, setUploading] = useState(false); // Indicates if upload is in progress

    // ✅ Remove this state:
    // const [showAvatarDropdown, setShowAvatarDropdown] = React.useState(false);

    const [selectedCountry, setSelectedCountry] = React.useState('');
    const [selectedCity, setSelectedCity] = React.useState('');

    const countryCityMap = {
        'Portugal': [
            'Lisbon', 'Porto', 'Braga', 'Coimbra', 'Faro',
            'Aveiro', 'Évora', 'Funchal', 'Portimão', 'Lagos',
            'Albufeira', 'Guimarães', 'Viana do Castelo', 'Viseu',
            'Nazaré', 'Peniche', 'Ericeira', 'Costa da Caparica',
            'Matosinhos', 'Vila do Conde', 'Espinho', 'Figueira da Foz'
        ],
        'Spain': ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Bilbao', 'San Sebastian'],
        'France': ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Bordeaux', 'Biarritz'],
        'Germany': ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Cologne', 'Dresden'],
        'Italy': ['Rome', 'Milan', 'Venice', 'Florence', 'Naples', 'Turin'],
        'Netherlands': ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Eindhoven'],
        'Belgium': ['Brussels', 'Antwerp', 'Ghent', 'Bruges', 'Liège'],
        'Greece': ['Athens', 'Thessaloniki', 'Patras', 'Heraklion', 'Rhodes'],
        'Ireland': ['Dublin', 'Cork', 'Galway', 'Limerick', 'Waterford'],
        'Sweden': ['Stockholm', 'Gothenburg', 'Malmö', 'Uppsala', 'Västerås'],
        'Denmark': ['Copenhagen', 'Aarhus', 'Odense', 'Aalborg', 'Esbjerg'],
        'Norway': ['Oslo', 'Bergen', 'Trondheim', 'Stavanger', 'Tromsø'],
        'Finland': ['Helsinki', 'Tampere', 'Turku', 'Oulu', 'Espoo'],
        'Switzerland': ['Zurich', 'Geneva', 'Basel', 'Bern', 'Lausanne'],
        'Brazil': ['Rio de Janeiro','São Paulo', 'Curitiba', 'Belo Horizonte', 'Florianópolis', 'Porto Alegre', 'São Luis']
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // ✅ Add or adapt these handlers for image upload/cropping
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        // Create a temporary URL for the cropper
        setFile(selectedFile);
        setCroppingURL(URL.createObjectURL(selectedFile));
        setShowCropper(true);
    };

    const handleCropCancel = () => {
        setShowCropper(false);
        setCroppingURL(null);
        setFile(null); // Clear selected file on cancel
    };

    const handleCropComplete = async (croppedBlob) => { // Cropper returns a Blob
        setShowCropper(false);
        setCroppingURL(null); // Clear cropping URL

        if (!croppedBlob) {
            setFile(null); // Clear file state if crop failed/empty
            return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
            console.error("No user is signed in for upload");
            setFile(null); // Clear file state
            alert("Authentication error during upload. Please try again.");
            return;
        }

        try {
            setUploading(true);
            // Generate a unique filename, e.g., using user ID and timestamp
            const filename = `profile_${currentUser.uid}_${Date.now()}.jpg`;
            const storageRef = ref(storage, `profilePictures/${currentUser.uid}/${filename}`);

            // Upload the blob to Firebase Storage
            const uploadResult = await uploadBytes(storageRef, croppedBlob);
            const downloadURL = await getDownloadURL(uploadResult.ref);

            // Update state with the new URL
            setProfileImageURL(downloadURL);
            setFile(null); // Clear file state after upload

            // ✅ Immediately update the Firestore document with the new image URL
            // Use setDoc with merge: true to only update the 'image' field
            await setDoc(doc(db, 'users', currentUser.uid), {
                image: downloadURL
            }, { merge: true });

            // ✅ Call onProfileUpdate immediately to update App.js state and header
            // Pass the updated user data structure expected by App.js's setUser
            onProfileUpdate({ image: downloadURL });


            console.log("Profile picture uploaded and updated in Firestore:", downloadURL);
            // Optional success feedback specific to image upload
            // alert("Profile picture updated!"); 

        } catch (error) {
            console.error("Error uploading profile picture:", error);
            alert("Failed to upload profile picture. Please try again.");
            setFile(null); // Clear file state on error
            setProfileImageURL(''); // Reset image URL state on error
        } finally {
            setUploading(false);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        // ✅ Extra strict user check
        if (!user || !user.uid) {
            console.error("User not authenticated");
            alert("Authentication error. Please try logging in again.");
            return;
        }

        // Check required fields (simplified check compared to original)
        if (!formData.name || !formData.age || !formData.gender || !formData.location || !formData.bio || !formData.instagram) {
             alert("Please fill in all required fields");
             return;
         }


        try {
            // Prepare data for Firestore
            const userData = {
                // ✅ Use profileImageURL if an image was uploaded and processed, 
                // otherwise use the default or potentially an existing one if this form was for editing.
                image: profileImageURL || formData.image || '/avatars/default.png',
                fullName: formData.name || '',
                age: formData.age || '',
                location: formData.location || '',
                gender: formData.gender || '',
                skills: {
                    hiking: formData.skills?.hiking || 'beginner',
                    running: formData.skills?.running || 'beginner'
                },
                bio: formData.bio || '',
                instagram: formData.instagram || '',
                email: user.email || '', // Email comes from auth user
                hasSeenOnboarding: true, // Mark onboarding complete here
                // createdAt should ideally be set only on initial creation in LoginView handleSignUp,
                // but setting it here with merge:true won't overwrite existing if it exists.
                createdAt: serverTimestamp()
            };

            console.log("Attempting to set user data for:", user.uid);
            console.log("With data:", userData);

            // Use setDoc with merge: true. If document exists (from handleSignUp), merge. If not, create.
            await setDoc(doc(db, 'users', user.uid), userData, { merge: true });


            // ✅ Call onProfileUpdate with ALL final user data from the form
            // This ensures the user object in App.js state is fully synced with all form fields
            onProfileUpdate(userData);


            // ✅ Call the new redirect prop to close the modal and navigate to home
            console.log("Registration successful!");
            onRegistrationCompleteRedirect();

            // ✅ Optionally, remove the old onComplete call entirely if it's not needed elsewhere
            // onComplete();

             // ✅ Optional: send welcome email - moved here to happen after full profile save
             try {
                 await emailjs.send(
                   process.env.REACT_APP_EMAILJS_SERVICE_ID,
                   process.env.REACT_APP_EMAILJS_WELCOME_TEMPLATE_ID,
                   {
                     to_email: user.email,
                     to_name: formData.name,
                   }
                 );
                 console.log("Welcome email sent successfully");
               } catch (emailError) {
                 console.error("Error sending welcome email:", emailError);
               }


        } catch (error) {
            console.error("Error saving user data:", error);
            console.error("Error details:", error.code, error.message);
            // alert("Error saving profile information. Please try again."); // Consider keeping this for user feedback
        }
    };

    const handleCountryChange = (e) => {
        const country = e.target.value;
        setSelectedCountry(country);
        setSelectedCity(''); // Reset city when country changes
        // Update formData.location here to clear the city portion
        setFormData(prev => ({
            ...prev,
            location: country // Store just country for now
        }));
    };

    const handleCityChange = (e) => {
        const city = e.target.value;
        setSelectedCity(city);
        // Update formData.location with both city and country
        setFormData(prev => ({
            ...prev,
            location: city ? `${city}, ${selectedCountry}` : selectedCountry // Use both city and country
        }));
    };


     // useEffect to fetch user data if editing (less relevant for initial registration, but keeps structure similar to PersonalProfileView)
     // For initial registration, user data is partially available from auth object via `user` prop.
     // A more complete fetch could happen here if needed to pre-fill more fields from DB on load.
      useEffect(() => {
         const fetchInitialData = async () => {
             if (user && user.uid) {
                 try {
                     const userDoc = await getDoc(doc(db, 'users', user.uid));
                     if (userDoc.exists()) {
                         const data = userDoc.data();
                         // Populate formData with existing data, merging with default structure
                         setFormData(prev => ({
                             ...prev,
                             ...data,
                             skills: {
                                 hiking: data.skills?.hiking || 'beginner',
                                 running: data.skills?.running || 'beginner'
                             },
                             // Handle existing image URL if present
                             image: data.image || '/avatars/default.png',
                             fullName: data.fullName || data.name || '', // Handle potential name field name difference
                         }));

                         // If location exists, set country/city dropdowns
                         if (data.location) {
                             const [city, country] = data.location.split(', ').reverse(); // Split and reverse to get [country, city]
                             setSelectedCountry(country || '');
                             setSelectedCity(city || '');
                         }

                         // Set profileImageURL if an image is already saved (e.g., if returning to this form)
                         if (data.image?.startsWith('http') || data.image?.startsWith('gs://')) { // Check for storage URLs
                             setProfileImageURL(data.image);
                         }

                     } else {
                         // If user doc doesn't exist (shouldn't happen after handleSignUp but safety)
                         console.log("User document not found, using default formData.");
                         // Initialize with auth user's display name if available
                         setFormData(prev => ({ ...prev, fullName: user.displayName || '' }));
                     }
                 } catch (error) {
                     console.error("Error fetching initial user data:", error);
                 }
             } else {
                console.log("No user or user ID available to fetch initial data.");
             }
         };

         fetchInitialData();
         // Dependence array ensures this runs when 'user' prop changes (e.g., after login)
     }, [user]); // Added user as dependency


    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            {/* ✅ Add Cropper Modal */}
            {showCropper && croppingURL && (
                <ImageCropper
                    imageSrc={croppingURL}
                    onCancel={handleCropCancel}
                    onComplete={handleCropComplete} // This handler now also uploads and updates state
                />
            )}

            <div style={{ maxHeight: '85vh', overflowY: 'auto', padding: '1rem', width: '100%', maxWidth: '400px' }}> {/* Added width/maxWidth */}
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Complete Your Profile
                    </h2>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6"> {/* Added margin top */}
                    <div className="rounded-md shadow-sm space-y-4">
                        {/* Profile Image Upload with Circle Display */}
                        <div className="text-center relative">
                            <label htmlFor="uploadImage" className="cursor-pointer inline-block">
                                {/* ✅ Use profileImageURL or a default */}
                                <img
                                    src={profileImageURL || formData.image || '/avatars/default.png'} // Use profileImageURL, fallback to formData.image, then a default
                                    alt="Profile Avatar"
                                    className="mx-auto h-32 w-32 rounded-full object-cover border-2 border-purple-600"
                                />
                                <p className="mt-2 text-sm text-purple-600 italic">
                                    {uploading ? 'Uploading...' : 'Click to upload profile photo'}
                                </p>
                            </label>

                            {/* Input is hidden, label makes the image clickable */}
                            <input
                                type="file"
                                id="uploadImage"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFileChange} // ✅ This triggers the cropper modal
                                disabled={uploading}
                            />
                            {/* ✅ Remove the "Upload Custom Image" button if handleCropComplete uploads */}
                            {/* Remove the button div here */}
                        </div>

                        {/* ✅ Remove the avatar selection dropdown */}
                        {/* Remove the div block containing the avatar images */}


                        {/* Other form fields - ensure they update formData */}
                        <div> {/* Wrap inputs/selects in divs for structure/spacing */}
                            <label htmlFor="fullName" className="sr-only">Full Name</label>
                            <input
                                id="fullName"
                                type="text"
                                name="name" // Ensure this matches formData key (should be 'name' from initial state)
                                placeholder="Full Name"
                                value={formData.name}
                                onChange={handleChange} // Use the generic handleChange
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                required
                            />
                        </div>
                        <div>
                             <label htmlFor="age" className="sr-only">Age</label>
                             <input
                                id="age"
                                type="number"
                                name="age" // Ensure this matches formData key
                                placeholder="Age"
                                value={formData.age}
                                onChange={handleChange} // Use the generic handleChange
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                required
                                min="1"
                                max="120"
                            />
                        </div>

                        <div>
                            <label htmlFor="gender" className="sr-only">Select Gender</label>
                             <select
                                id="gender"
                                name="gender" // Ensure this matches formData key
                                value={formData.gender}
                                onChange={handleChange} // Use the generic handleChange
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                required
                            >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                         <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Location</label> {/* Added a label for clarity */}
                             {/* Country Select */}
                             <select
                                name="country"
                                value={selectedCountry}
                                onChange={handleCountryChange} // This handler updates selectedCountry state and triggers location update
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                required
                            >
                                <option value="">Select Country</option>
                                {Object.keys(countryCityMap).map(country => (
                                    <option key={country} value={country}>
                                        {country}
                                    </option>
                                ))}
                            </select>

                             {/* City Select */}
                             <select
                                name="city"
                                value={selectedCity}
                                onChange={handleCityChange} // This handler updates selectedCity state and updates formData.location
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                disabled={!selectedCountry} // Disable city select until country is chosen
                                required
                            >
                                <option value="">Select City</option>
                                {selectedCountry && countryCityMap[selectedCountry].map(city => (
                                    <option key={city} value={city}>
                                        {city}
                                    </option>
                                ))}
                            </select>
                        </div>


                        <div className="space-y-4">
                            {/* Skills Selects - ensure they update formData.skills */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Hiking Experience</label>
                                <select
                                    name="hikingSkill" // Name doesn't strictly matter for formData update here, but good practice
                                    value={formData.skills.hiking}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        skills: {
                                            ...formData.skills,
                                            hiking: e.target.value.toLowerCase()
                                        }
                                    })}
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                    required
                                >
                                    <option value="beginner">Beginner</option>
                                    <option value="intermediate">Intermediate</option>
                                    <option value="advanced">Advanced</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Running Experience</label>
                                <select
                                    name="runningSkill" // Name doesn't strictly matter for formData update here, but good practice
                                    value={formData.skills.running}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        skills: {
                                            ...formData.skills,
                                            running: e.target.value.toLowerCase()
                                        }
                                    })}
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                    required
                                >
                                    <option value="beginner">Beginner</option>
                                    <option value="intermediate">Intermediate</option>
                                    <option value="advanced">Advanced</option>
                                </select>
                            </div>
                        </div>

                        <div>
                             <label htmlFor="bio" className="sr-only">Bio</label>
                             <textarea
                                id="bio"
                                name="bio" // Ensure this matches formData key
                                placeholder="Bio"
                                value={formData.bio}
                                onChange={handleChange} // Use the generic handleChange
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                rows="3"
                                required
                                minLength="10"
                                maxLength="500"
                            />
                        </div>

                        <div>
                             <label htmlFor="instagram" className="sr-only">Instagram handle</label>
                             <input
                                id="instagram"
                                type="text"
                                name="instagram" // Ensure this matches formData key
                                placeholder="Instagram handle (e.g., yourusername)"
                                value={formData.instagram}
                                onChange={handleChange} // Use the generic handleChange
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                        disabled={uploading} // Disable submit button while uploading image
                    >
                       {uploading ? 'Uploading Image...' : 'Complete Registration'}
                    </button>
                </form>

            </div>
        </div>
    );
};

export default RegisterForm;